export type DesktopAppRole = "organizer" | "player";

export interface DesktopRouteRedirectRule {
  /** Route pattern (exact or with `:param`, e.g. `/competition/:id/schedule`) */
  from: string;
  /** Target route pattern (can interpolate `:param`, e.g. `/competition/:id/manage-results`) */
  to: string;
}

export interface RoleDesktopRoutePolicy {
  /** Default landing route when entering bare tab root or blocked route on Desktop */
  defaultRedirect: string;
  /** Bare index routes that redirect to defaultRedirect on Desktop */
  redirectBarePaths: readonly string[];
  /**
   * Explicit route redirects on Desktop to eliminate redundant/duplicate Mobile screens.
   * Supports `:param` placeholders (e.g. `/competition/:id/schedule` -> `/competition/:id/manage-results`).
   */
  redirectRules: readonly DesktopRouteRedirectRule[];
  /**
   * Routes (exact or pattern with `:param`) that are disabled on Desktop for this role
   * and fall back to `defaultRedirect`.
   */
  disabledRoutes: readonly string[];
}

export type DesktopRoutePolicyMap = Record<
  DesktopAppRole,
  RoleDesktopRoutePolicy
>;

export const DESKTOP_ROUTE_POLICY: DesktopRoutePolicyMap = {
  organizer: {
    defaultRedirect: "/organizer-tabs/my-tournaments",
    redirectBarePaths: ["/organizer-tabs"],
    redirectRules: [
      // The desktop sidebar has no newsfeed. Route it to the organized-tournaments archive.
      {
        from: "/organizer-tabs/newsfeed",
        to: "/organizer-tabs/my-tournaments",
      },
      // NOTE: `/competition/:id/schedule` -> `manage-results` and `/competition/:id/list` -> `registration-list`
      // are intentionally NOT static rules here: they only apply when the user manages that specific
      // competition (ownership), which a role-based policy cannot know. They are handled inside the
      // desktop views via `useCompetitionManagementAccess`.
      // Trang Cài đặt (/settings) là menu trung gian của Mobile, gộp thẳng về Quản lý tài khoản (/account)
      {
        from: "/settings",
        to: "/account",
      },
    ],
    disabledRoutes: [],
  },
  player: {
    defaultRedirect: "/tabs/tournaments",
    redirectBarePaths: ["/tabs"],
    redirectRules: [
      // Bảng tin (/tabs/newsfeed) trên Mobile chỉ là trang tóm tắt giải đấu, gộp về Khám phá giải đấu (/tabs/tournaments)
      {
        from: "/tabs/newsfeed",
        to: "/tabs/tournaments",
      },
      // Lịch thi đấu & Lịch sử trận đấu dùng chung dữ liệu và bộ lọc tại /tabs/schedule
      {
        from: "/match-history",
        to: "/tabs/schedule",
      },
      {
        from: "/my-schedule",
        to: "/tabs/schedule",
      },
      // Thành tựu cá nhân đã được tích hợp trực tiếp vào trang Thống kê & Thành tựu (/tabs/statistics) trên Desktop
      {
        from: "/player-achievements",
        to: "/tabs/statistics",
      },
      // Trang Cài đặt (/settings) là menu trung gian của Mobile, gộp thẳng về Quản lý tài khoản (/account)
      {
        from: "/settings",
        to: "/account",
      },
    ],
    disabledRoutes: [],
  },
};

const normalizePathname = (pathname: string): string => {
  if (!pathname) return "/";
  const withoutQuery = pathname.split("?")[0]?.split("#")[0] || "/";
  if (withoutQuery.length > 1 && withoutQuery.endsWith("/")) {
    return withoutQuery.slice(0, -1);
  }
  return withoutQuery;
};

export const extractRouteParams = (
  pathname: string,
  pattern: string,
): Record<string, string> | null => {
  const cleanPath = normalizePathname(pathname);
  const cleanPattern = normalizePathname(pattern);

  if (cleanPath === cleanPattern) {
    return {};
  }

  const pathSegments = cleanPath.split("/").filter(Boolean);
  const patternSegments = cleanPattern.split("/").filter(Boolean);

  if (pathSegments.length !== patternSegments.length) {
    return null;
  }

  const params: Record<string, string> = {};
  for (let i = 0; i < patternSegments.length; i += 1) {
    const patternSegment = patternSegments[i];
    const pathSegment = pathSegments[i];
    if (!patternSegment || !pathSegment) {
      return null;
    }
    if (patternSegment.startsWith(":")) {
      params[patternSegment.slice(1)] = pathSegment;
    } else if (patternSegment !== pathSegment) {
      return null;
    }
  }

  return params;
};

export const matchesRoutePattern = (
  pathname: string,
  pattern: string,
): boolean => extractRouteParams(pathname, pattern) !== null;

const interpolateRouteParams = (
  targetPattern: string,
  params: Record<string, string>,
): string => {
  return targetPattern.replace(/:([A-Za-z0-9_]+)/g, (_, key: string) => {
    return params[key] ?? `:${key}`;
  });
};

export function resolveDesktopRouteRedirect(
  pathname: string,
  role: DesktopAppRole = "organizer",
  policyMap: DesktopRoutePolicyMap = DESKTOP_ROUTE_POLICY,
): string | null {
  const normalized = normalizePathname(pathname);
  const policy = policyMap[role] ?? policyMap.organizer;

  if (
    policy.redirectBarePaths.some((barePath) =>
      matchesRoutePattern(normalized, barePath),
    )
  ) {
    return policy.defaultRedirect;
  }

  for (const rule of policy.redirectRules ?? []) {
    const params = extractRouteParams(normalized, rule.from);
    if (params !== null) {
      return interpolateRouteParams(rule.to, params);
    }
  }

  if (
    policy.disabledRoutes.some((disabledPattern) =>
      matchesRoutePattern(normalized, disabledPattern),
    )
  ) {
    return policy.defaultRedirect;
  }

  return null;
}

export function isDesktopRouteEnabled(
  pathname: string,
  role: DesktopAppRole = "organizer",
  policyMap: DesktopRoutePolicyMap = DESKTOP_ROUTE_POLICY,
): boolean {
  return resolveDesktopRouteRedirect(pathname, role, policyMap) === null;
}

/**
 * Routes that only exist for organizers (tournament management tools).
 * Single source of truth used by the router guard, the desktop shell and the
 * competition workspace bar to decide which sidebar/role context to show.
 */
const ORGANIZER_WORKSPACE_PREFIXES = [
  "/organizer-tabs",
  "/create-tournament",
  "/edit-bracket",
  "/draw-position",
  "/random",
  "/registration-list",
] as const;

const ORGANIZER_WORKSPACE_SUFFIXES = ["/manage-results", "/add-prize"] as const;

export function isOrganizerWorkspacePath(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  return (
    ORGANIZER_WORKSPACE_PREFIXES.some((prefix) =>
      normalized.startsWith(prefix),
    ) ||
    ORGANIZER_WORKSPACE_SUFFIXES.some((suffix) => normalized.endsWith(suffix))
  );
}

/**
 * Resolves which desktop role context (sidebar) to show for a path.
 * Organizer tool routes always use the organizer context; player tab routes always
 * use the player context; shared routes fall back to the user's stored role.
 */
export function resolveDesktopRoleForPath(
  pathname: string,
  storedRole: string | null | undefined,
): DesktopAppRole {
  if (isOrganizerWorkspacePath(pathname)) return "organizer";
  if (normalizePathname(pathname).startsWith("/tabs")) return "player";
  return storedRole === "Organizer" ? "organizer" : "player";
}
